/**
 * MilestoneShareDialog — visuel partageable d'un palier franchi.
 * Choix du format (Story 9:16 / Carré 1:1), option "afficher mon agence",
 * aperçu live, téléchargement PNG et partage natif (Web Share API).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Download, Share2, Loader2, Smartphone, Square } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function MilestoneShareDialog({ milestone, open, onClose }) {
    const [format, setFormat] = useState('story');
    const [withAgency, setWithAgency] = useState(true);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sharing, setSharing] = useState(false);
    const blobRef = useRef(null);

    const fetchImage = useCallback(async () => {
        if (!milestone) return;
        setLoading(true);
        try {
            const r = await api.get('/cockpit/milestones/certificate', {
                params: { code: milestone.code, format, with_agency: withAgency },
                responseType: 'blob',
            });
            blobRef.current = r.data;
            setPreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return URL.createObjectURL(r.data);
            });
        } catch (e) {
            toast.error("Impossible de générer le visuel. Réessayez.");
        } finally { setLoading(false); }
    }, [milestone, format, withAgency]);

    useEffect(() => {
        if (open && milestone) fetchImage();
    }, [open, milestone, fetchImage]);

    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    if (!milestone) return null;

    const filename = `palier-homly-${milestone.code}-${format}.png`;

    const download = () => {
        if (!blobRef.current) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blobRef.current);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('Visuel téléchargé — prêt à publier !');
    };

    const share = async () => {
        if (!blobRef.current) return;
        const file = new File([blobRef.current], filename, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            setSharing(true);
            try {
                await navigator.share({
                    files: [file],
                    title: 'Palier franchi chez Homly',
                    text: `${milestone.label} — Certifié Homly`,
                });
            } catch (e) { /* annulé par l'utilisateur */ }
            setSharing(false);
        } else {
            download();
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="milestone-share-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-[#F5B301]" />
                        Partager ce palier
                    </DialogTitle>
                    <DialogDescription>{milestone.label}</DialogDescription>
                </DialogHeader>

                {/* Choix du format */}
                <div className="grid grid-cols-2 gap-2" data-testid="milestone-format-choice">
                    <button
                        type="button"
                        onClick={() => setFormat('story')}
                        data-testid="milestone-format-story"
                        className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                            format === 'story'
                                ? 'border-[#F5B301] bg-[#F5B301]/15 text-[#F5B301]'
                                : 'border-border text-muted-foreground hover:border-[#F5B301]/50'
                        }`}
                    >
                        <Smartphone className="w-4 h-4" />
                        Story <span className="text-[10px] opacity-70">9:16</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setFormat('square')}
                        data-testid="milestone-format-square"
                        className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                            format === 'square'
                                ? 'border-[#F5B301] bg-[#F5B301]/15 text-[#F5B301]'
                                : 'border-border text-muted-foreground hover:border-[#F5B301]/50'
                        }`}
                    >
                        <Square className="w-4 h-4" />
                        Photo <span className="text-[10px] opacity-70">1:1</span>
                    </button>
                </div>

                {/* Option agence */}
                <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
                    <span className="text-sm text-foreground">Afficher mon agence</span>
                    <Switch
                        checked={withAgency}
                        onCheckedChange={setWithAgency}
                        data-testid="milestone-with-agency-switch"
                    />
                </div>

                {/* Aperçu */}
                <div
                    className="flex items-center justify-center rounded-xl overflow-hidden bg-black/30"
                    style={{ minHeight: 260 }}
                    data-testid="milestone-preview"
                >
                    {loading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-[#F5B301]" />
                    ) : previewUrl ? (
                        <img
                            src={previewUrl}
                            alt={`Visuel du palier : ${milestone.label}`}
                            className="max-h-[340px] w-auto rounded-lg shadow-lg"
                        />
                    ) : null}
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={download}
                        disabled={loading || !previewUrl}
                        data-testid="milestone-download-btn"
                        className="flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border border-border text-foreground hover:bg-white/5 transition disabled:opacity-50"
                    >
                        <Download className="w-4 h-4" />
                        Télécharger
                    </button>
                    <button
                        type="button"
                        onClick={share}
                        disabled={loading || sharing || !previewUrl}
                        data-testid="milestone-native-share-btn"
                        className="flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold text-[#2A0A3D] transition disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #F5B301, #FF914D)' }}
                    >
                        {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                        Partager
                    </button>
                </div>
                <p className="text-[11px] text-muted-foreground text-center -mt-1">
                    Sur mobile, « Partager » ouvre directement Instagram, Facebook, WhatsApp…
                </p>
            </DialogContent>
        </Dialog>
    );
}
